import React from "react";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Typography,
  ButtonBase,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  designColors,
  designFontFamily,
  designType,
  ICON_TINT_ALPHA,
  navRowContainerSx,
  navRowTextSx,
} from "services/constants/designTokens";

// Single, shared implementation of the left request-nav rail used by BOTH
// My Zone > My Requests (MyZoneApply) and Team Zone > Approve. Before this
// existed each screen hand-rolled the same List/ListItemButton/ButtonBase
// tree with its own copy of every font size, weight, padding and color — and
// they drifted (e.g. the child rows' font-family). Rendering both from ONE
// component means the two rails are byte-for-byte identical by construction:
// change the look here and both move together.
//
// Each screen still owns its own data and behavior and passes it in:
//   sections        [{ title, items:[{ title, key, color, icon, count, children:[{ title, key }] }] }]
//   width           rail width in px
//   isItemActive    (item)        => boolean
//   isChildActive   (item, child) => boolean
//   onItemClick     (item)        => void
//   onChildClick    (item, child) => void
//   sideBarOpen     collapse toggle (width -> 0 when false)
export const RequestNavRail = ({
  sections,
  width,
  isItemActive,
  isChildActive,
  onItemClick,
  onChildClick,
  sideBarOpen = true,
  comfortable = false,
}) => (
  <Box
    sx={{
      borderRadius: 0,
      width: sideBarOpen ? width : 0,
      transition: "width 0.3s ease",
      overflowX: "hidden",
      flexShrink: 0,
      backgroundColor: designColors.white,
      // Tied to sideBarOpen like width above, not unconditional — a border
      // still renders at its own 1px even on a width:0 box, so with Approve
      // now keeping this permanently collapsed (see sideBarOpen usage
      // there), this line was sitting directly against the main app
      // Sidebar's own right edge with no rail width left to separate them,
      // reading as two close parallel lines instead of one clean boundary.
      borderRight: sideBarOpen
        ? `1px solid ${designColors.slate200}`
        : "none",
    }}
  >
    <List sx={{ p: comfortable ? 1 : 0, fontFamily: designFontFamily }}>
      {sections.map((section, sectionIndex) => (
        <Box
          key={`section-${sectionIndex}`}
          sx={{
            pt: sectionIndex === 0 ? (comfortable ? 1.75 : 1.5) : 0.5,
            pb: 1.5,
            borderBottom:
              sectionIndex < sections.length - 1
                ? `1px solid ${designColors.slate100}`
                : "none",
          }}
        >
          {section.title ? (
            <Typography
              sx={{
                fontFamily: designFontFamily,
                px: 3,
                pb: comfortable ? 2 : 1,
                // Matches designType.sectionHeading — the same bold, dark
                // section-header treatment used elsewhere (e.g. Reports'
                // "Report Categories") — instead of the faint, small
                // eyebrow-label look this previously had on its own.
                ...designType.sectionHeading,
              }}
            >
              {section.title}
            </Typography>
          ) : null}

          {section.items.map((item) => {
            const active = isItemActive(item);
            const hasChildren =
              Array.isArray(item.children) && item.children.length > 0;

            return (
              <Box key={item.key}>
                <ListItem disablePadding sx={{ mb: comfortable ? 0.75 : 0.5 }}>
                  <ListItemButton
                    onClick={() => onItemClick(item)}
                    sx={{
                      alignItems: comfortable ? "center" : "flex-start",
                      px: 2.25,
                      py: comfortable ? 1.25 : 1.2,
                      // Container look shared verbatim with the main app
                      // Sidebar via navRowContainerSx — see that function's
                      // comment for why. Selection uses ONE consistent brand
                      // color for every item (same tealDark/tealTint pair as
                      // accentSelectedRow elsewhere in the app) instead of
                      // each item's own category color — so "what's
                      // selected" reads at a glance regardless of which row
                      // it is. Category color (item.color) is reserved for
                      // the count badge below, which is a category
                      // indicator, not a selection one.
                      ...navRowContainerSx({ active, depth: "top" }),
                      "&:hover": {
                        backgroundColor: active
                          ? designColors.tealTint
                          : designColors.slate100,
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 34,
                        // Selected rows still read via ONE consistent teal
                        // (matches the row's own border/background/text) so
                        // "what's active" stays unambiguous. At rest, each
                        // item's icon now takes its own category `color`
                        // (already defined by both callers — Approve's
                        // approval types, My Requests' request types), at
                        // ICON_TINT_ALPHA (shared with the main Sidebar's
                        // own icon tint) rather than the full brand hex —
                        // bright and distinct per category, short of fully
                        // saturated so tealDark still reads as the one
                        // color that means "selected."
                        color: active
                          ? designColors.tealDark
                          : alpha(
                              item.color || designColors.slate400,
                              ICON_TINT_ALPHA,
                            ),
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <Typography
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        lineHeight: 1.3,
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        // Text look shared with the main app Sidebar via
                        // navRowTextSx.
                        ...navRowTextSx({ active, depth: "top" }),
                      }}
                    >
                      {item.title}
                    </Typography>
                  </ListItemButton>
                </ListItem>

                {hasChildren ? (
                  <Box sx={{ px: 4.5, pb: 0.75 }}>
                    {item.children.map((child) => {
                      const childActive = isChildActive(item, child);
                      return (
                        <ButtonBase
                          key={`${item.key}-${child.title}`}
                          onClick={() => onChildClick(item, child)}
                          sx={{
                            width: "100%",
                            justifyContent: "flex-start",
                            textAlign: "left",
                            py: 0.85,
                            // Shared with the main app Sidebar's "nested"
                            // level (which already has this selection
                            // background/border) — children here previously
                            // had neither, an inconsistency this refactor
                            // also closes.
                            ...navRowContainerSx({
                              active: childActive,
                              depth: "nested",
                            }),
                            ...navRowTextSx({
                              active: childActive,
                              depth: "nested",
                            }),
                          }}
                        >
                          {child.title}
                        </ButtonBase>
                      );
                    })}
                  </Box>
                ) : null}
              </Box>
            );
          })}
        </Box>
      ))}
    </List>
  </Box>
);
